import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getMedicalProfileDirectiveCacheKey, longTermCacheStaleTime, onProfileDirectiveUpdate } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfileDirectives', {
    failedToLoadCodeStatus: 'Failed to load code status',
    failedToLoadPOA: 'Failed to load Legal Decision Maker',
    failedToUpdateCodeStatus: 'Failed to update code status',
    failedToUpdatePOA: 'Failed to update Legal Decision Maker',
    successfulUpdateCodeStatus: 'Successfully updated code status',
    successfulUpdatePOA: 'Successfully updated Legal Decision Maker',
    successfulDeleteCodeStatus: 'Successfully deleted code status',
    successfulDeletePOA: 'Successfully deleted Legal Decision Maker',
    failedToDeleteCodeStatus: 'Failed to delete code status',
    failedToDeletePOA: 'Failed to delete Legal Decision Maker',
});

export type UseMedicalProfileDirectivesArgs = {
    medicalProfileId: string;
    onCodeStatusUpdateSuccess?: () => void;
    onPOAUpdateSuccess?: () => void;
    onDeletedCodeStatus?: () => void;
    onDeletedPOA?: () => void;
};

export type UseMedicalProfileDirectivesReturnValue = {
    codeStatus: APITypes.medicalProfileService.GetProfileCodeStatusResponse | null;
    poa: APITypes.medicalProfileService.GetProfilePOAResponse | null;
    isLoadingCodeStatus: boolean;
    isLoadingPOA: boolean;
    updateCodeStatus: (codeStatus: APITypes.medicalProfileService.UpsertProfileCodeStatusRequest) => void;
    isUpdatingCodeStatus: boolean;
    updatePOA: (poa: APITypes.medicalProfileService.UpsertProfilePOARequest) => void;
    isUpdatingPOA: boolean;
    deleteCodeStatus: () => void;
    isDeletingCodeStatus: boolean;
    deletePOA: () => void;
    isDeletingPOA: boolean;
};

export const useMedicalProfileDirectives = ({
    medicalProfileId,
    onCodeStatusUpdateSuccess,
    onPOAUpdateSuccess,
    onDeletedCodeStatus,
    onDeletedPOA,
}: UseMedicalProfileDirectivesArgs): UseMedicalProfileDirectivesReturnValue => {
    const notify = useNotify();

    const codeStatusQuery = useQuery({
        queryKey: getMedicalProfileDirectiveCacheKey(medicalProfileId, 'code-status'),
        staleTime: longTermCacheStaleTime,
        queryFn: async () => {
            const response = await appAPI.get<APITypes.medicalProfileService.GetProfileCodeStatusResponse>(
                urls.api.directives.codeStatus(medicalProfileId),
            );
            return response;
        },
    });
    useQueryErrorNotify(codeStatusQuery, intl('failedToLoadCodeStatus'));

    const poaQuery = useQuery({
        queryKey: getMedicalProfileDirectiveCacheKey(medicalProfileId, 'poa'),
        staleTime: longTermCacheStaleTime,
        queryFn: async () => {
            const response = await appAPI.get<APITypes.medicalProfileService.GetProfilePOAResponse>(
                urls.api.directives.poa(medicalProfileId),
            );
            return response;
        },
    });
    useQueryErrorNotify(poaQuery, intl('failedToLoadPOA'));

    const deleteCodeStatusMutation = useMutation({
        mutationFn: async () => {
            await appAPI.del(urls.api.directives.codeStatus(medicalProfileId));
        },
        onError: () => {
            notify('error', intl('failedToDeleteCodeStatus'));
        },
        onSuccess: () => {
            notify('success', intl('successfulDeleteCodeStatus'));
            onProfileDirectiveUpdate.resetCache({ medicalProfileId, directiveType: 'code-status' })();
            onDeletedCodeStatus?.();
        },
    });

    const deletePOAMutation = useMutation({
        mutationFn: async () => {
            await appAPI.del(urls.api.directives.poa(medicalProfileId));
        },
        onError: () => {
            notify('error', intl('failedToDeletePOA'));
        },
        onSuccess: () => {
            notify('success', intl('successfulDeletePOA'));
            onProfileDirectiveUpdate.resetCache({ medicalProfileId, directiveType: 'poa' })();
            onDeletedPOA?.();
        },
    });

    const updateCodeStatusMutation = useMutation({
        mutationFn: async (codeStatus: APITypes.medicalProfileService.UpsertProfileCodeStatusRequest) => {
            const response = await appAPI.put<APITypes.medicalProfileService.GetProfileCodeStatusResponse>(
                urls.api.directives.codeStatus(medicalProfileId),
                { body: codeStatus },
            );
            return response;
        },
        onError: () => {
            notify('error', intl('failedToUpdateCodeStatus'));
        },
        onSuccess: () => {
            notify('success', intl('successfulUpdateCodeStatus'));
            onProfileDirectiveUpdate.resetCache({ medicalProfileId, directiveType: 'code-status' })();
            onCodeStatusUpdateSuccess?.();
        },
    });

    const updatePOAMutation = useMutation({
        mutationFn: async (poa: APITypes.medicalProfileService.UpsertProfilePOARequest) => {
            const response = await appAPI.put<APITypes.medicalProfileService.GetProfilePOAResponse>(
                urls.api.directives.poa(medicalProfileId),
                { body: poa },
            );
            return response;
        },
        onError: () => {
            notify('error', intl('failedToUpdatePOA'));
        },
        onSuccess: () => {
            notify('success', intl('successfulUpdatePOA'));
            onProfileDirectiveUpdate.resetCache({ medicalProfileId, directiveType: 'poa' })();
            onPOAUpdateSuccess?.();
        },
    });

    return {
        codeStatus: codeStatusQuery.data || null,
        poa: poaQuery.data || null,
        isLoadingCodeStatus: codeStatusQuery.isLoading,
        isLoadingPOA: poaQuery.isLoading,
        updateCodeStatus: updateCodeStatusMutation.mutate,
        isUpdatingCodeStatus: updateCodeStatusMutation.isPending,
        updatePOA: updatePOAMutation.mutate,
        isUpdatingPOA: updatePOAMutation.isPending,
        deleteCodeStatus: deleteCodeStatusMutation.mutate,
        isDeletingCodeStatus: deleteCodeStatusMutation.isPending,
        deletePOA: deletePOAMutation.mutate,
        isDeletingPOA: deletePOAMutation.isPending,
    };
};
